import React, { CSSProperties } from 'react';
import { Flex, GridAlpha } from '@applyboard/crystal-ui';
import { StepDefinition } from './workflow/LoanFormStepper';

interface TimelineStepperProps {
  stepDefinitions: StepDefinition[];
  currentStep: number;
}

const GREEN = '#087C69';
const BLUE = '#0064E1';
const GREY = '#D7DAE8';
const DARKER_GREY = '#A9B1CC';
const GREY_05 = '#363B51';
const GREY_10 = '#616D92';

const TimelineStepper: React.FC<TimelineStepperProps> = ({ currentStep, stepDefinitions }) => {
  const lineStyle: CSSProperties = {
    height: '2px',
    backgroundColor: GREY,
    position: 'relative',
    width: '100%',
    zIndex: 0,
  };

  const nodeStyle = (isCompleted: boolean, isInProgress: boolean): CSSProperties => ({
    width: '30px',
    height: '30px',
    borderRadius: '50%',
    backgroundColor: isCompleted ? GREEN : isInProgress ? BLUE : 'white',
    border: isCompleted || isInProgress ? `none` : `3px solid ${DARKER_GREY}`,
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1rem',
    position: 'relative',
    zIndex: 2,
  });

  const textStyle = (isCompleted: boolean, isInProgress: boolean): CSSProperties => ({
    marginTop: '8px',
    color: isCompleted || isInProgress ? GREY_05 : GREY_10,
    fontWeight: 600,
    fontFamily: 'Open Sans',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  });

  return (
    <Flex direction="column" align="center" pt={10} pb={{ sm: 0, md: 4 }}>
      <div
        style={{
          display: 'flex',
          position: 'relative',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: '40px',
        }}
      >
        {stepDefinitions.map((step, index) => {
          const isCompleted = index < currentStep;
          const isInProgress = index === currentStep;
          return (
            <GridAlpha rows="repeat(2, 1fr)" key={`TimelineGrid-${index}`}>
              <GridAlpha columns="repeat(11, 1fr)" key="TimelineGridNodeAndLines">
                <GridAlpha.Item columnStart={1} columnEnd={6} align="center">
                  {index === 0 ? undefined : (
                    <div
                      style={{
                        ...lineStyle,
                        backgroundColor: isInProgress || isCompleted ? GREEN : GREY,
                      }}
                    />
                  )}
                </GridAlpha.Item>
                <GridAlpha.Item columnStart={6} columnEnd={7}>
                  <div style={nodeStyle(isCompleted, isInProgress)}>
                    {isCompleted ? '✓' : undefined}
                  </div>
                </GridAlpha.Item>
                <GridAlpha.Item columnStart={7} columnEnd={12} align="center">
                  {index === stepDefinitions.length - 1 ? undefined : (
                    <div
                      style={{
                        ...lineStyle,
                        backgroundColor: isCompleted ? GREEN : GREY,
                      }}
                    />
                  )}
                </GridAlpha.Item>
              </GridAlpha>

              <GridAlpha columns="repeat(11, 1fr)" key="TimelineGridTextDescription">
                <GridAlpha.Item columnStart={1} columnEnd={12} align="center">
                  <Flex hideBelow="md" align="center">
                    <div style={{ ...textStyle(isCompleted, isInProgress) }}>{step.name}</div>
                  </Flex>
                </GridAlpha.Item>
              </GridAlpha>
            </GridAlpha>
          );
        })}
      </div>
      <Flex hideAbove="md" align="center">
        <div style={{ color: GREY_05, fontWeight: 600, fontFamily: 'Open Sans' }}>
          Step {currentStep + 1} of {stepDefinitions.length}: {stepDefinitions[currentStep].name}
        </div>
      </Flex>
    </Flex>
  );
};

export default TimelineStepper;
